import React, { useState } from 'react';
import { Link } from 'react-router-dom';
import { useNavigate } from 'react-router-dom';
import { useQueryClient } from '@tanstack/react-query';
import { useAuth } from '../../auth/AuthContext';
import { useSubscriptionContext } from '../../contexts/SubscriptionContext';
import { useMyOrganization } from '../../hooks/useMyOrganization';
import SandboxConversionModal from './SandboxConversionModal';
import { getSandboxBannerMessage, isSandboxOrganization, isSandboxReadOnly } from '../../utils/sandboxUtils';
import { organizationKeys } from '../../utils/queryClient';
import { FiAlertCircle, FiClock, FiX } from 'react-icons/fi';
import { isClientRole } from '../../auth/roleUtils';

const TRIAL_BANNER_DISMISSAL_PREFIX = 'subscriptionTrialBannerDismissed';

function formatDate(dateStr) {
    if (!dateStr) return '';
    return new Date(dateStr).toLocaleDateString('en-US', {
        month: 'long',
        day: 'numeric',
        year: 'numeric',
    });
}

function BannerShell({ children, className, onDismiss, dismissLabel }) {
    return (
        <div className="fixed bottom-0 left-0 right-0 z-10 md:z-40">
            <div className={`${className} border-t px-4 py-3 text-sm shadow-[0_-1px_4px_rgba(0,0,0,0.04)] sm:py-2`}>
                <div className={`relative mx-auto flex max-w-6xl flex-col gap-2 sm:flex-row sm:items-center sm:justify-center ${onDismiss ? 'pr-8' : ''}`}>
                    {children}
                    {onDismiss && (
                        <button
                            type="button"
                            onClick={onDismiss}
                            className="absolute right-0 top-0 rounded p-1 text-current opacity-70 transition hover:bg-black/5 hover:opacity-100 focus:outline-none focus:ring-2 focus:ring-current focus:ring-offset-1"
                            aria-label={dismissLabel}
                        >
                            <FiX className="h-4 w-4" />
                        </button>
                    )}
                </div>
            </div>
        </div>
    );
}

function BannerMessage({ icon: Icon, children }) {
    return (
        <div className="flex min-w-0 items-start gap-2 sm:items-center">
            <Icon className="mt-0.5 h-4 w-4 flex-shrink-0 sm:mt-0" />
            <span className="min-w-0 leading-snug">{children}</span>
        </div>
    );
}

export default function SubscriptionBanner() {
    const [showConversionModal, setShowConversionModal] = useState(false);
    const [dismissedTrialKey, setDismissedTrialKey] = useState(null);
    const { login: loginWithToken } = useAuth();
    const navigate = useNavigate();
    const queryClient = useQueryClient();
    const { subscription, loading, refetch: refetchSubscription } = useSubscriptionContext();
    const { data: organization, isLoading: organizationLoading, refetch: refetchOrganization } = useMyOrganization();
    const isClientUser = isClientRole(localStorage.getItem('userRole'));

    const getTrialDismissalKey = (trialEnd) => {
        const organizationIdentity = organization?.referenceId || organization?.id || 'current-organization';
        return `${TRIAL_BANNER_DISMISSAL_PREFIX}:${organizationIdentity}:${trialEnd}`;
    };

    const dismissTrialBanner = (trialEnd) => {
        const dismissalKey = getTrialDismissalKey(trialEnd);

        try {
            localStorage.setItem(dismissalKey, 'true');
        } catch (error) {
            console.warn('Could not persist trial banner dismissal', error);
        }

        setDismissedTrialKey(dismissalKey);
    };

    const handleConverted = async (result) => {
        const token = result?.token || result?.accessToken || result?.authToken;
        if (token) {
            localStorage.setItem('velaToken', token);
            loginWithToken(token);
        }

        const organizationResult = await refetchOrganization();
        await refetchSubscription?.();
        queryClient.invalidateQueries({ queryKey: organizationKeys.my() });

        setShowConversionModal(false);

        const refreshedOrganization = organizationResult?.data;
        if (!isSandboxOrganization(refreshedOrganization)) {
            navigate('/midwife/billing/plans', { replace: true });
        }
    };

    if (isClientUser) {
        return null;
    }

    if (!organizationLoading && isSandboxOrganization(organization)) {
        const isReadOnly = isSandboxReadOnly(organization);

        return (
            <>
                <BannerShell className="border-blue-200 bg-blue-50 text-blue-900">
                    <BannerMessage icon={FiAlertCircle}>
                        {getSandboxBannerMessage(organization)}
                    </BannerMessage>
                    <button
                        type="button"
                        onClick={() => setShowConversionModal(true)}
                        className={`self-start whitespace-nowrap font-medium underline hover:text-blue-700 sm:self-center ${isReadOnly ? 'text-blue-900' : ''}`}
                    >
                        Start your practice
                    </button>
                </BannerShell>
                <SandboxConversionModal
                    isOpen={showConversionModal}
                    organization={organization}
                    onClose={() => setShowConversionModal(false)}
                    onConverted={handleConverted}
                />
            </>
        );
    }

    if (loading || !subscription) return null;

    const { status, trialEnd, currentPeriodEnd, isRecordsOnly, accessMode, requiresCheckout } = subscription;

    if (isRecordsOnly || accessMode === 'RecordsOnly') {
        return (
            <BannerShell className="border-slate-300 bg-slate-50 text-slate-900">
                <BannerMessage icon={FiAlertCircle}>
                    Records-only access is enabled. Existing clinical records can be viewed and exported, but changes and day-to-day tools are disabled.
                </BannerMessage>
                <Link to="/midwife/clients" className="self-start whitespace-nowrap font-medium underline hover:text-slate-700 sm:self-center">
                    View records
                </Link>
            </BannerShell>
        );
    }

    if (status === 'Trialing' && trialEnd) {
        const dismissalKey = getTrialDismissalKey(trialEnd);
        let wasPreviouslyDismissed = dismissedTrialKey === dismissalKey;

        try {
            wasPreviouslyDismissed = wasPreviouslyDismissed || localStorage.getItem(dismissalKey) === 'true';
        } catch {
            // The banner remains dismissible for the current page when storage is unavailable.
        }

        if (wasPreviouslyDismissed) return null;

        return (
            <BannerShell
                className="border-amber-200 bg-amber-50 text-amber-900"
                onDismiss={() => dismissTrialBanner(trialEnd)}
                dismissLabel="Dismiss trial notice"
            >
                <BannerMessage icon={FiClock}>
                    Trial ends on {formatDate(trialEnd)}
                </BannerMessage>
                <Link to="/midwife/billing/plans" className="self-start whitespace-nowrap font-medium underline hover:text-amber-700 sm:self-center">
                    View plans
                </Link>
            </BannerShell>
        );
    }

    if (status === 'PastDue') {
        return (
            <BannerShell className="border-red-200 bg-red-50 text-red-900">
                <BannerMessage icon={FiAlertCircle}>
                    Payment failed &mdash; please update your payment method
                </BannerMessage>
                <Link to="/midwife/billing" className="self-start whitespace-nowrap font-medium underline hover:text-red-700 sm:self-center">
                    Update now
                </Link>
            </BannerShell>
        );
    }

    if (status === 'Canceled' && currentPeriodEnd && new Date(currentPeriodEnd) > new Date()) {
        return (
            <BannerShell className="border-amber-200 bg-amber-50 text-amber-900">
                <BannerMessage icon={FiAlertCircle}>
                    Your subscription has been canceled. Access continues until {formatDate(currentPeriodEnd)}
                </BannerMessage>
                <Link to="/midwife/billing/plans" className="self-start whitespace-nowrap font-medium underline hover:text-amber-700 sm:self-center">
                    Resubscribe
                </Link>
            </BannerShell>
        );
    }

    if (requiresCheckout) {
        return (
            <BannerShell className="border-amber-200 bg-amber-50 text-amber-900">
                <BannerMessage icon={FiAlertCircle}>
                    Your subscription has ended. Resubscribe to restore access.
                </BannerMessage>
                <Link to="/midwife/billing/plans" className="self-start whitespace-nowrap font-medium underline hover:text-amber-700 sm:self-center">
                    Resubscribe
                </Link>
            </BannerShell>
        );
    }

    return null;
}
